import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FlyoutOption } from 'visana-ui-library';
import { Franchise } from '../../core/api';
import { UntypedFormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, Observable, skip } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  adultFranchises,
  childrenFranchises,
  getLabelForFranchise,
  isAdultFranchise,
} from '../../core/models/franchise.model';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-coverage-selection',
  templateUrl: './coverage-selection.component.html',
  styleUrls: ['coverage-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoverageSelectionComponent implements OnInit, OnChanges {
  @Input() public initialFranchise: Franchise;
  @Input() public initialAccidentCoverage: boolean = false;

  @Output() public franchiseSelected = new EventEmitter<Franchise>();
  @Output() public accidentCoverageSelected = new EventEmitter<boolean>();

  public includesAccidentCoverageOptions$: Observable<FlyoutOption<boolean>[]>;
  public franchiseOptions$: Observable<FlyoutOption<Franchise>[]>;
  public franchiseExpandableDescription$: Observable<string>;

  public formControlFranchise: UntypedFormControl;
  public formControlAccidentCoverage: UntypedFormControl;
  private isAdultFranchise$: Observable<boolean>;
  private isAdultSubject$: BehaviorSubject<boolean>;

  constructor(private translation: TranslateService) {}

  public ngOnInit(): void {
    this.includesAccidentCoverageOptions$ = this.translation.onLangChange.pipe(
      startWith(''),
      map(() => [
        {
          label: this.translation.instant('BASIC_INSURANCE.ACCIDENT_COVERAGE.WITH'),
          value: true,
        },
        {
          label: this.translation.instant('BASIC_INSURANCE.ACCIDENT_COVERAGE.WITHOUT'),
          value: false,
        },
      ]),
    );
    const initialIsAdult = isAdultFranchise(this.initialFranchise);
    this.isAdultSubject$ = new BehaviorSubject<boolean>(initialIsAdult);
    this.isAdultFranchise$ = this.isAdultSubject$.pipe(distinctUntilChanged());

    this.franchiseExpandableDescription$ = combineLatest([
      this.translation.onLangChange.pipe(startWith(undefined)),
      this.isAdultFranchise$,
    ]).pipe(map(([_, isAdult]) => this.getFranchiseExpandableDescription(isAdult)));

    this.franchiseOptions$ = this.isAdultFranchise$.pipe(
      map((isAdult: boolean) => this.getFranchiseFlyoutOptions(isAdult)),
    );

    this.formControlFranchise = new UntypedFormControl(this.initialFranchise);
    this.formControlAccidentCoverage = new UntypedFormControl(this.initialAccidentCoverage);

    // emit only changes. prevent emitting when default is selected on first pick
    this.formControlFranchise.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(this.initialFranchise),
        filter((value) => value !== null),
        skip(1),
      )
      .subscribe((franchise: Franchise) => {
        this.franchiseSelected.emit(franchise);
      });
    this.formControlAccidentCoverage.valueChanges
      .pipe(untilDestroyed(this), startWith(this.initialAccidentCoverage), skip(1))
      .subscribe((covered: boolean) => this.accidentCoverageSelected.emit(covered));
  }

  private getFranchiseFlyoutOptions(isAdult: boolean): FlyoutOption<Franchise>[] {
    const franchises = isAdult ? adultFranchises : childrenFranchises;

    return franchises.map((franchise) => ({
      value: franchise,
      label: getLabelForFranchise(franchise),
    }));
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // prevent calling before onInit
    if (changes['initialFranchise']?.firstChange === false) {
      this.isAdultSubject$.next(isAdultFranchise(this.initialFranchise));
      // prevent setting value before new franchise options are set on filter bar component
      setTimeout(() => {
        this.formControlFranchise.setValue(this.initialFranchise, {
          emitEvent: false,
        });
      }, 0);
    }
    if (changes['initialAccidentCoverage']?.firstChange === false) {
      this.formControlAccidentCoverage.setValue(this.initialAccidentCoverage, {
        emitEvent: false,
      });
    }
  }

  private getFranchiseExpandableDescription(isAdultFranchiseValue: boolean): string {
    if (isAdultFranchiseValue) {
      return (
        '<ul>' +
        '<li>' +
        this.translation.instant('BASIC_INSURANCE.FRANCHISE.DESCRIPTION_ADULT_300') +
        '</li>' +
        '<li>' +
        this.translation.instant('BASIC_INSURANCE.FRANCHISE.DESCRIPTION_ADULT_2500') +
        '</li>' +
        '</ul>'
      );
    } else {
      return (
        '<ul>' + '<li>' + this.translation.instant('BASIC_INSURANCE.FRANCHISE.DESCRIPTION_CHILD_0') + '</li>' + '</ul>'
      );
    }
  }
}
